import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useBulkAddTagsMutation } from '~entities/parent/api/bulk-add-tags';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { type TagsOption, TagsSelector } from '~features/shared/tags-selector';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Label } from '~shared/shadcn/ui/label';

interface BulkAddTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedParents: Array<{ _id: string; name: string }>;
  onSuccess?: () => void;
}

export const BulkAddTagDialog = ({
  open,
  onOpenChange,
  selectedParents,
  onSuccess,
}: BulkAddTagDialogProps) => {
  const [selectedTags, setSelectedTags] = useState<Array<TagsOption>>([]);

  const { data: studioDetails } = useGetStudioDetails();
  const { mutate: bulkAddTags, isPending } = useBulkAddTagsMutation();

  const handleBulkAddTags = async () => {
    if (selectedTags.length === 0) {
      toast.error('Please select at least one tag');
      return;
    }

    if (!studioDetails?._id) {
      toast.error('Studio details not available');
      return;
    }

    const parentIds = selectedParents.map((parent) => parent._id);
    let totalAdded = 0;
    let totalFailed = 0;

    // Call bulk endpoint once per tag
    for (const tag of selectedTags) {
      bulkAddTags(
        {
          parentIds,
          tagId: tag.value,
          studioId: studioDetails._id,
        },
        {
          onSuccess: (response) => {
            totalAdded += response.added;
            totalFailed += response.failed;

            if (response.errors && response.errors.length > 0) {
              console.warn(
                `Errors adding tag "${tag.label}":`,
                response.errors,
              );
            }

            // If this is the last tag, show final summary
            if (selectedTags.indexOf(tag) === selectedTags.length - 1) {
              if (totalFailed === 0) {
                toast.success(
                  `Successfully added ${selectedTags.length} ${selectedTags.length === 1 ? 'tag' : 'tags'} to ${totalAdded / selectedTags.length} ${totalAdded / selectedTags.length === 1 ? 'family' : 'families'}`,
                );
              } else if (totalAdded === 0) {
                toast.error('Failed to add tags to all families');
              } else {
                toast.warning(
                  `Added tags with ${totalAdded} successes and ${totalFailed} failures`,
                );
              }

              setSelectedTags([]);
              onOpenChange(false);
              onSuccess?.();
            }
          },
          onError: (error) => {
            console.error(`Failed to add tag "${tag.label}":`, error);
            totalFailed += parentIds.length;

            // If this is the last tag, show error
            if (selectedTags.indexOf(tag) === selectedTags.length - 1) {
              toast.error('Failed to add tags to families');
              setSelectedTags([]);
              onOpenChange(false);
            }
          },
        },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='overflow-visible'>
        <DialogHeader>
          <DialogTitle>Add Tags to Families</DialogTitle>
          <DialogDescription>
            Add tags to {selectedParents.length} selected{' '}
            {selectedParents.length === 1 ? 'family' : 'families'}
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label>Selected Families ({selectedParents.length})</Label>
            <div className='max-h-32 overflow-y-auto rounded-md border p-2'>
              <ul className='space-y-1 text-sm text-muted-foreground'>
                {selectedParents.map((parent) => (
                  <li key={parent._id} className='capitalize'>
                    {parent.name}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className='space-y-2'>
            <Label>Select Tags to Add</Label>
            <TagsSelector
              value={selectedTags}
              onChange={(value) => {
                setSelectedTags(value ?? []);
              }}
              menuPortalTarget={null}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline' disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={() => void handleBulkAddTags()}
            disabled={isPending || selectedTags.length === 0}
          >
            {isPending && <Loader2 className='mr-2 size-4 animate-spin' />}
            {isPending ? 'Adding Tags...' : 'Add Tags'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
